import type { ReportLocalGovernmentFundsTableFundOwnerForLocalGovernmentModel } from '@/features/mold/models';
import { reportLocalGovernmentFundTableFundOwnerForLocalGovernmentCsvHeader } from '@/features/mold/models/local-government-fund';
import { t } from '@/i18n';
import {
  convertToNegative,
  createCsv,
  formatAmountForCsv,
  validateTotalWithElements,
} from '@/utils';
import type { z } from 'zod';

const getReportTargetYear = (fiscalYear: number) => {
  return fiscalYear - 1;
};

export const getFundOwnerForLocalGovernmentCsvHeader = (
  fiscalYear: number,
): Record<
  z.infer<
    typeof reportLocalGovernmentFundTableFundOwnerForLocalGovernmentCsvHeader
  >,
  string
> => {
  const reportYear = getReportTargetYear(fiscalYear);

  return {
    number: t('report-fund-owner-for-local-government-index'),
    localFundOwner: t('fund-owner-for-local-government'),
    localFundName: t('fund-business-name'),
    localFundOverview: t('project-overview'),
    beginningOfTheFundBalance: t(
      'report-local-government-fund-header-beginning-of-the-fund-balance',
      { year: reportYear - 1 },
    ),
    nationalEquivalentOfBeginningOfTheFundBalance: `${t(
      'report-local-government-fund-header-fund-balance',
      { year: reportYear - 1 },
    )}${t('amount-equivalent-to-national-expense')}`,
    incomeAmount: `${t('fiscal-year-with-value', { year: reportYear })}${t('report-local-government-fund-header-income')}`,
    nationalEquivalentOfIncomeAmount: `${t('fiscal-year-with-value', { year: reportYear })}${t('income')}${t('amount-equivalent-to-national-expense')}`,
    grantedAmountByTheCountryFromInitial: `${t('fiscal-year-with-value', { year: reportYear })}${t(
      'amount-of-funding-from-the-government',
    )}（${t('initial')}）`,
    grantedAmountByTheCountryFromSupplementary: `${t('fiscal-year-with-value', { year: reportYear })}${t(
      'amount-of-funding-from-the-government',
    )}（${t('correction')}）`,
    grantedAmountByTheCountryFromReserve: `${t('fiscal-year-with-value', { year: reportYear })}${t(
      'amount-of-funding-from-the-government',
    )}（${t('reserve-budget')}）`,
    otherIncomeAmount: `${t('fiscal-year-with-value', { year: reportYear })}${t('income')}${t('others')}`,
    businessExpense: `${t('fiscal-year-with-value', { year: reportYear })}${t('report-local-government-fund-header-expense')}`,
    amountReturnedToTheNationalTreasury: t(
      'report-local-government-fund-header-amount-of-national-treasury-return',
      { year: reportYear },
    ),
    endingOfTheFundBalance: t(
      'report-local-government-fund-header-ending-of-the-fund-balance',
      { year: reportYear },
    ),
    nationalEquivalentOfTheEndOfTheFundBalance: `${t(
      'report-local-government-fund-header-fund-balance',
      { year: reportYear },
    )}${t('amount-equivalent-to-national-expense')}`,
    decidedNumberOfSubsidy: `${t('report-local-government-fund-header-decision-on-project-implementation', { year: reportYear })}:${t('fund-business-subsidy')}${t('report-local-government-fund-header-supplementary-for-local-government-fund-business')}（${t('count')}）`,
    decidedAmountOfSubsidy: `${t('report-local-government-fund-header-decision-on-project-implementation', { year: reportYear })}:${t('fund-business-subsidy')}${t('report-local-government-fund-header-supplementary-for-local-government-fund-business')}（${t('amount')}）`,
    decidedNumberOfInvestment: `${t('report-local-government-fund-header-decision-on-project-implementation', { year: reportYear })}:${t('investment')}（${t('count')}）`,
    decidedAmountOfInvestment: `${t('report-local-government-fund-header-decision-on-project-implementation', { year: reportYear })}:${t('investment')}（${t('amount')}）`,
    decidedNumberOfLoan: `${t('report-local-government-fund-header-decision-on-project-implementation', { year: reportYear })}:${t('loan')}（${t('count')}）`,
    decidedAmountOfLoan: `${t('report-local-government-fund-header-decision-on-project-implementation', { year: reportYear })}:${t('loan')}（${t('amount')}）`,
    decidedNumberOfDebtGuarantee: `${t('report-local-government-fund-header-decision-on-project-implementation', { year: reportYear })}:${t('debt-guarantee')}（${t('count')}）`,
    decidedAmountOfDebtGuarantee: `${t('report-local-government-fund-header-decision-on-project-implementation', { year: reportYear })}:${t('debt-guarantee')}（${t('amount')}）`,
    decidedNumberOfOthers: `${t('report-local-government-fund-header-decision-on-project-implementation', { year: reportYear })}:${t('fund-business-for-local-government-others')}（${t('count')}）`,
    decidedAmountOfOthers: `${t('report-local-government-fund-header-decision-on-project-implementation', { year: reportYear })}:${t('fund-business-for-local-government-others')}（${t('amount')}）`,
    balanceNumberOfInvestment: `${t('report-local-government-fund-header-loan-balance', { year: reportYear })}:${t('investment')}（${t('count')}）`,
    balanceAmountOfInvestment: `${t('report-local-government-fund-header-loan-balance', { year: reportYear })}:${t('investment')}（${t('amount')}）`,
    balanceNumberOfLoan: `${t('report-local-government-fund-header-loan-balance', { year: reportYear })}:${t('loan')}（${t('count')}）`,
    balanceAmountOfLoan: `${t('report-local-government-fund-header-loan-balance', { year: reportYear })}:${t('loan')}（${t('amount')}）`,
    balanceNumberOfDebtGuarantee: `${t('report-local-government-fund-header-loan-balance', { year: reportYear })}:${t('debt-guarantee')}（${t('count')}）`,
    balanceAmountOfDebtGuarantee: `${t('report-local-government-fund-header-loan-balance', { year: reportYear })}:${t('debt-guarantee')}（${t('amount')}）`,
  };
};

export const getFundOwnerForLocalGovernmentCsv = ({
  fiscalYear,
  reports,
  isDebugMode,
}: {
  fiscalYear: number;
  reports: z.infer<
    typeof ReportLocalGovernmentFundsTableFundOwnerForLocalGovernmentModel
  >;
  isDebugMode: boolean;
}) => {
  const headers = getFundOwnerForLocalGovernmentCsvHeader(fiscalYear);

  const csvFormattedReports = reports.data.map((report) => {
    // summaryDataのnegativeCountを使用
    const summary = reports.summaryData;

    const { result: isNullBeginningOfTheFundBalance } =
      validateTotalWithElements({
        totalValue: report.endingOfTheFundBalance,
        elements: [
          {
            value: report.beginningOfTheFundBalance,
            rawValue: report.beginningOfTheFundBalance,
            negativeCount: 0,
          },
          {
            value: report.incomeAmount,
            rawValue: report.incomeAmount,
            negativeCount: 0,
          },
          {
            value: convertToNegative(report.businessExpense),
            rawValue: convertToNegative(report.businessExpense),
            negativeCount: 0,
          },
          {
            value: convertToNegative(
              report.amountReturnedToTheNationalTreasury,
            ),
            rawValue: convertToNegative(
              report.amountReturnedToTheNationalTreasury,
            ),
            negativeCount: 0,
          },
        ],
      });

    return {
      ...report,
      beginningOfTheFundBalance: formatAmountForCsv(
        report.beginningOfTheFundBalance,
        {
          isDebugMode,
          negativeCount: summary?.negativeBeginningOfTheFundBalanceCount,
        },
      ),
      nationalEquivalentOfBeginningOfTheFundBalance: formatAmountForCsv(
        report.nationalEquivalentOfBeginningOfTheFundBalance,
        {
          isDebugMode,
          negativeCount:
            summary?.negativeNationalEquivalentOfBeginningOfTheFundBalanceCount,
        },
      ),
      incomeAmount: formatAmountForCsv(report.incomeAmount, {
        isDebugMode,
        negativeCount: summary?.negativeIncomeAmountCount,
      }),
      nationalEquivalentOfIncomeAmount: formatAmountForCsv(
        report.nationalEquivalentOfIncomeAmount,
        {
          isDebugMode,
          negativeCount: summary?.negativeNationalEquivalentOfIncomeAmountCount,
        },
      ),
      grantedAmountByTheCountryFromInitial: formatAmountForCsv(
        report.grantedAmountByTheCountryFromInitial,
        {
          isDebugMode,
          negativeCount:
            summary?.negativeGrantedAmountByTheCountryFromInitialCount,
        },
      ),
      grantedAmountByTheCountryFromSupplementary: formatAmountForCsv(
        report.grantedAmountByTheCountryFromSupplementary,
        {
          isDebugMode,
          negativeCount:
            summary?.negativeGrantedAmountByTheCountryFromSupplementaryCount,
        },
      ),
      grantedAmountByTheCountryFromReserve: formatAmountForCsv(
        report.grantedAmountByTheCountryFromReserve,
        {
          isDebugMode,
          negativeCount:
            summary?.negativeGrantedAmountByTheCountryFromReserveCount,
        },
      ),
      otherIncomeAmount: formatAmountForCsv(report.otherIncomeAmount, {
        isDebugMode,
        negativeCount: summary?.negativeOtherIncomeAmountCount,
      }),
      businessExpense: formatAmountForCsv(report.businessExpense, {
        isDebugMode,
        negativeCount: summary?.negativeBusinessExpenseCount,
      }),
      amountReturnedToTheNationalTreasury: formatAmountForCsv(
        report.amountReturnedToTheNationalTreasury,
        {
          isDebugMode,
          negativeCount:
            summary?.negativeAmountReturnedToTheNationalTreasuryCount,
        },
      ),
      endingOfTheFundBalance: formatAmountForCsv(
        report.endingOfTheFundBalance,
        {
          isDebugMode,
          negativeCount:
            isNullBeginningOfTheFundBalance === 'isNull' ||
            summary?.negativeEndingOfTheFundBalanceCount
              ? 1
              : 0,
        },
      ),
      nationalEquivalentOfTheEndOfTheFundBalance: formatAmountForCsv(
        report.nationalEquivalentOfTheEndOfTheFundBalance,
        {
          isDebugMode,
          negativeCount:
            summary?.negativeNationalEquivalentOfTheEndOfTheFundBalanceCount,
        },
      ),
      decidedAmountOfSubsidy: formatAmountForCsv(
        report.decidedAmountOfSubsidy,
        {
          isDebugMode,
          negativeCount: summary?.negativeDecidedAmountOfSubsidyCount,
        },
      ),
      decidedAmountOfInvestment: formatAmountForCsv(
        report.decidedAmountOfInvestment,
        {
          isDebugMode,
          negativeCount: summary?.negativeDecidedAmountOfInvestmentCount,
        },
      ),
      decidedAmountOfLoan: formatAmountForCsv(report.decidedAmountOfLoan, {
        isDebugMode,
        negativeCount: summary?.negativeDecidedAmountOfLoanCount,
      }),
      decidedAmountOfDebtGuarantee: formatAmountForCsv(
        report.decidedAmountOfDebtGuarantee,
        {
          isDebugMode,
          negativeCount: summary?.negativeDecidedAmountOfDebtGuaranteeCount,
        },
      ),
      decidedAmountOfOthers: formatAmountForCsv(report.decidedAmountOfOthers, {
        isDebugMode,
        negativeCount: summary?.negativeDecidedAmountOfOthersCount,
      }),
      balanceAmountOfInvestment: formatAmountForCsv(
        report.balanceAmountOfInvestment,
        {
          isDebugMode,
          negativeCount: summary?.negativeBalanceAmountOfInvestmentCount,
        },
      ),
      balanceAmountOfLoan: formatAmountForCsv(report.balanceAmountOfLoan, {
        isDebugMode,
        negativeCount: summary?.negativeBalanceAmountOfLoanCount,
      }),
      balanceAmountOfDebtGuarantee: formatAmountForCsv(
        report.balanceAmountOfDebtGuarantee,
        {
          isDebugMode,
          negativeCount: summary?.negativeBalanceAmountOfDebtGuaranteeCount,
        },
      ),
    };
  });

  const csvData = createCsv({
    headerKeys:
      reportLocalGovernmentFundTableFundOwnerForLocalGovernmentCsvHeader.options,
    headerConverter: headers,
    body: csvFormattedReports,
  });

  return csvData;
};
